/**
 * MergeModal - Modal for manually merging duplicate contacts or companies
 *
 * Displays merge candidates, allows selecting a target, and shows preview
 * of what will be merged before confirming.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { contactsApi, companiesApi, MergeCandidate, Contact, Company } from '../../services/contactsApi';

type EntityType = 'contact' | 'company';

interface MergeModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: EntityType;
  sourceEntity: Contact | Company;
  onMergeComplete: (mergedEntity: Contact | Company) => void;
}

const MergeModal: React.FC<MergeModalProps> = ({
  isOpen,
  onClose,
  entityType,
  sourceEntity,
  onMergeComplete,
}) => {
  const { t } = useTranslation('people');
  const [candidates, setCandidates] = useState<MergeCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<MergeCandidate | null>(null);
  const [merging, setMerging] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<MergeCandidate[]>([]);
  const [searching, setSearching] = useState(false);

  // Load merge candidates when modal opens
  const loadCandidates = useCallback(async () => {
    if (!sourceEntity?.id) return;

    setLoading(true);
    setError(null);

    try {
      const api = entityType === 'contact' ? contactsApi : companiesApi;
      const response = await api.getMergeCandidates(sourceEntity.id);
      setCandidates(response?.candidates || []);
    } catch (err) {
      console.error('Failed to load merge candidates:', err);
      setError('Failed to load potential duplicates');
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [sourceEntity?.id, entityType]);

  useEffect(() => {
    if (isOpen && sourceEntity) {
      loadCandidates();
      setSelectedCandidate(null);
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [isOpen, sourceEntity, loadCandidates]);

  // Search for additional merge targets
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      if (entityType === 'contact') {
        const results = await contactsApi.search(searchQuery, 10);
        // Filter out the source contact and convert to MergeCandidate format
        const filtered = (results || [])
          .filter(r => r.id !== sourceEntity.id)
          .map(r => ({
            id: r.id,
            name: r.full_name,
            email: r.primary_email,
            avatar_url: r.avatar_url,
            title: r.title,
            similarity_score: 0,
            match_reason: 'Search result',
          }));
        setSearchResults(filtered);
      } else {
        // For companies, use semantic search with includeCompanies
        const response = await contactsApi.searchSemantic(searchQuery, {
          limit: 10,
          mode: 'hybrid',
          includeCompanies: true,
        });
        const filtered = (response?.results || [])
          .filter(r => r.entity_type === 'company' && r.id !== sourceEntity.id)
          .map(r => ({
            id: r.id,
            name: r.name || r.full_name || '',
            domain: undefined,
            similarity_score: r.similarity || 0,
            match_reason: 'Search result',
          }));
        setSearchResults(filtered);
      }
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Perform the merge
  const handleMerge = async () => {
    if (!selectedCandidate) return;

    setMerging(true);
    setError(null);

    try {
      const api = entityType === 'contact' ? contactsApi : companiesApi;
      const result = await api.manualMerge(sourceEntity.id, selectedCandidate.id);

      if (result.success) {
        onMergeComplete(result.merged_entity);
        onClose();
      } else {
        setError(result.message || 'Merge failed');
      }
    } catch (err) {
      console.error('Merge failed:', err);
      setError('Failed to merge entities');
    } finally {
      setMerging(false);
    }
  };

  if (!isOpen) return null;

  const sourceName = entityType === 'contact'
    ? (sourceEntity as Contact).full_name
    : (sourceEntity as Company).name;

  const sourceEmail = entityType === 'contact'
    ? (sourceEntity as Contact).primary_email
    : (sourceEntity as Company).domain;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              {t('merge_modal.title', { entityType: entityType === 'contact' ? t('merge_modal.contact') : t('merge_modal.company') })}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {t('merge_modal.merging_into', { name: sourceName, entityType })}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Source Entity Info */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <div className="text-sm text-blue-700 font-medium mb-1">{t('merge_modal.source_label')}</div>
            <div className="flex items-center gap-3">
              {entityType === 'contact' && (sourceEntity as Contact).avatar_url && (
                <img
                  src={(sourceEntity as Contact).avatar_url}
                  alt={sourceName}
                  className="w-10 h-10 rounded-full object-cover"
                />
              )}
              <div>
                <div className="font-medium text-gray-900">{sourceName}</div>
                {sourceEmail && (
                  <div className="text-sm text-gray-500">{sourceEmail}</div>
                )}
              </div>
            </div>
          </div>

          {/* Search for merge target */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('merge_modal.search_label')}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder={t('merge_modal.search_placeholder', { entityType: entityType === 'contact' ? t('merge_modal.contacts') : t('merge_modal.companies') })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={handleSearch}
                disabled={searching || !searchQuery.trim()}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                {searching ? t('merge_modal.searching') : t('merge_modal.search')}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {/* Suggested Candidates */}
          {loading ? (
            <div className="text-center py-8 text-gray-500">
              {t('merge_modal.loading_duplicates')}
            </div>
          ) : (
            <>
              {candidates && candidates.length > 0 && (
                <div className="mb-4">
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    {t('merge_modal.suggested_duplicates', { count: candidates.length })}
                  </div>
                  <div className="space-y-2">
                    {candidates.map((candidate) => (
                      <CandidateCard
                        key={candidate.id}
                        candidate={candidate}
                        entityType={entityType}
                        isSelected={selectedCandidate?.id === candidate.id}
                        onSelect={() => setSelectedCandidate(candidate)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Search Results */}
              {searchResults && searchResults.length > 0 && (
                <div className="mb-4">
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    {t('merge_modal.search_results', { count: searchResults.length })}
                  </div>
                  <div className="space-y-2">
                    {searchResults.map((candidate) => (
                      <CandidateCard
                        key={candidate.id}
                        candidate={candidate}
                        entityType={entityType}
                        isSelected={selectedCandidate?.id === candidate.id}
                        onSelect={() => setSelectedCandidate(candidate)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {(!candidates || candidates.length === 0) && (!searchResults || searchResults.length === 0) && !loading && (
                <div className="text-center py-8 text-gray-500">
                  {t('merge_modal.no_duplicates')}
                </div>
              )}
            </>
          )}

          {/* Selected Target Preview */}
          {selectedCandidate && (
            <div className="mt-6 bg-green-50 rounded-lg p-4">
              <div className="text-sm text-green-700 font-medium mb-2">
                {t('merge_modal.target_label')}
              </div>
              <div className="flex items-center gap-3">
                {entityType === 'contact' && selectedCandidate.avatar_url && (
                  <img
                    src={selectedCandidate.avatar_url}
                    alt={selectedCandidate.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                )}
                <div>
                  <div className="font-medium text-gray-900">{selectedCandidate.name}</div>
                  {(selectedCandidate.email || selectedCandidate.domain) && (
                    <div className="text-sm text-gray-500">
                      {selectedCandidate.email || selectedCandidate.domain}
                    </div>
                  )}
                </div>
              </div>

              {/* What will be merged */}
              <div className="mt-4 text-sm text-gray-600">
                <div className="font-medium text-gray-700 mb-1">{t('merge_modal.what_merged')}:</div>
                <ul className="list-disc list-inside space-y-1">
                  <li>{t('merge_modal.merge_items.emails')}</li>
                  <li>{t('merge_modal.merge_items.phones')}</li>
                  {entityType === 'contact' && (
                    <>
                      <li>{t('merge_modal.merge_items.company_associations')}</li>
                      <li>{t('merge_modal.merge_items.knowledge_cards')}</li>
                    </>
                  )}
                  {entityType === 'company' && (
                    <>
                      <li>{t('merge_modal.merge_items.employees')}</li>
                      <li>{t('merge_modal.merge_items.addresses')}</li>
                      <li>{t('merge_modal.merge_items.knowledge_cards')}</li>
                    </>
                  )}
                  <li>{t('merge_modal.merge_items.alias')}</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-amber-600">
            <svg className="w-5 h-5 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {t('merge_modal.cannot_undo')}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              {t('merge_modal.cancel')}
            </button>
            <button
              onClick={handleMerge}
              disabled={!selectedCandidate || merging}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {merging ? t('merge_modal.merging') : t('merge_modal.merge')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Candidate card component
interface CandidateCardProps {
  candidate: MergeCandidate;
  entityType: EntityType;
  isSelected: boolean;
  onSelect: () => void;
}

const CandidateCard: React.FC<CandidateCardProps> = ({
  candidate,
  entityType,
  isSelected,
  onSelect,
}) => {
  return (
    <div
      onClick={onSelect}
      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
        isSelected
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center gap-3">
        {entityType === 'contact' && candidate.avatar_url && (
          <img
            src={candidate.avatar_url}
            alt={candidate.name}
            className="w-8 h-8 rounded-full object-cover"
          />
        )}
        {entityType === 'company' && (
          <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-medium">
            {candidate.name.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 truncate">{candidate.name}</div>
          <div className="text-sm text-gray-500 truncate">
            {candidate.email || candidate.domain || candidate.title || ''}
          </div>
        </div>
        {candidate.similarity_score > 0 && (
          <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
            {Math.round(candidate.similarity_score * 100)}% match
          </div>
        )}
        {candidate.match_reason && candidate.match_reason !== 'Search result' && (
          <div className="text-xs text-blue-600">
            {candidate.match_reason}
          </div>
        )}
      </div>
    </div>
  );
};

export default MergeModal;
